import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";

import "../styles/generalStyle.css";
import "./Songs.css";
import { useLanguage } from "../i18n/LanguageContext";

import { doc, setDoc, updateDoc, increment, serverTimestamp } from "firebase/firestore";



export async function clearSongNotifs(uid) {
  const ref = doc(db, "usuarios", uid, "notifs", "summary");
  await setDoc(
    ref,
    {
      songUpdates: 0,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}


export default function Songs() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [firebaseUser, setFirebaseUser] = useState(() => auth.currentUser);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  // Player state
  const audioRef = useRef(null);
  const [playingKey, setPlayingKey] = useState(null); // `${songId}:v1` | `${songId}:v2`
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setFirebaseUser(u || null));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!firebaseUser) {
      setLoading(false);
      setItems([]);
      return;
    }

    setLoading(true);
    setError("");

    const ref = collection(db, "usuarios", firebaseUser.uid, "canciones");
    const q = query(ref, orderBy("fechaCreacion", "desc"));
    clearSongNotifs(firebaseUser.uid);

    const unsub = onSnapshot(
      q,
      (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems(arr);
        setLoading(false);
      },
      (e) => {
        console.error("Error leyendo canciones:", e);
        setError(t("songs.list.errorLoad"));
        setLoading(false);
      }
    );

    return () => unsub();
  }, [firebaseUser, t]);

  // cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
      }
    };
  }, []);

  const hasItems = useMemo(() => items.length > 0, [items]);

  const statusLabel = (s) => {
    if (!s) return "—";
    const v = String(s).toLowerCase();
    if (v === "pending") return t("songs.item.statusPending");
    if (v === "done") return t("songs.item.statusDone");
    if (v === "error") return t("songs.item.statusError");
    return s;
  };

  const ensureAudio = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = "none";
      audioRef.current.addEventListener("ended", () => {
        setIsPlaying(false);
        setPlayingKey(null);
      });
      audioRef.current.addEventListener("pause", () => setIsPlaying(false));
      audioRef.current.addEventListener("play", () => setIsPlaying(true));
    }
    return audioRef.current;
  };

  const handleTogglePlay = async ({ songId, version, url }) => {
    const key = `${songId}:${version}`;

    if (!url) return;

    const audio = ensureAudio();

    try {
      // if clicking the same currently playing -> toggle pause
      if (playingKey === key && !audio.paused) {
        audio.pause();
        return;
      }

      // switch track
      setPlayingKey(key);
      audio.pause();
      audio.src = url;
      await audio.play();
    } catch (e) {
      console.error("Audio play error:", e);
      setError(t("songs.list.playError"));
      setIsPlaying(false);
      setPlayingKey(null);
    }
  };

  const goDetails = (songId) => {
    navigate(`/app/songs/${songId}`);
  };

  return (
    <div className="page">
      <div className="container">
        {/* Header */}
        <div className="songsHeader">
          <div>
            <h1 className="h1 songsTitle">
              <span className="songsTitleIcon" aria-hidden>🎧</span>
              {t("songs.list.title")}
            </h1>
            <p className="p-muted" style={{ marginTop: 6 }}>
              {t("songs.list.subtitle")}
            </p>
          </div>

          <button
            className="btn btn-primary"
            onClick={() => navigate("/app/crear-cancion")}
          >
            {t("songs.list.createButton")}
          </button>
        </div>

        {/* State */}
        {loading ? (
          <div className="card">
            <div className="profile-loading" style={{ marginTop: 0 }}>
              <div className="spinner" />
              <p>{t("songs.list.loading")}</p>
            </div>
          </div>
        ) : error ? (
          <div className="card">
            <div className="alert-error">{error}</div>
          </div>
        ) : !hasItems ? (
          <div className="card">
            <div className="emptyBox">
              <div className="emptyIcon" aria-hidden>✨</div>
              <div className="emptyTitle">{t("songs.list.emptyTitle")}</div>
              <div className="emptyDesc">{t("songs.list.emptyDesc")}</div>

              <button
                className="btn btn-primary"
                style={{ marginTop: 12 }}
                onClick={() => navigate("/app/crear-cancion")}
              >
                {t("songs.list.emptyCta")}
              </button>
            </div>
          </div>
        ) : (
          <div className="songsList">
            {items.map((it) => {
              const tituloBase = (it.titulo || "").trim() || t("songs.item.untitled");
              const estilo = (it.estilo || "").trim() || "—";
              const status = statusLabel(it.status);

              const v1Url = (it.coverUrl || "").trim();
              const v2Url = (it.coverUrl02 || "").trim();

              const renderVersionRow = (version, url) => {
                const key = `${it.id}:${version}`;
                const active = playingKey === key && isPlaying;

                return (
                  <div className={`songVersionRow ${active ? "active" : ""}`}>
                    <button
                      type="button"
                      className={`playBtn ${active ? "playing" : ""}`}
                      onClick={() => handleTogglePlay({ songId: it.id, version, url })}
                      disabled={!url}
                      title={url ? t("songs.item.play") : t("songs.item.notAvailable")}
                    >
                      {active ? "⏸" : "▶"}
                    </button>

                    <div className="songVersionInfo">
                      <div className="songVersionTitle">
                        {tituloBase} <span className="songVersionTag">{version === "v1" ? "V1" : "V2"}</span>
                      </div>
                      <div className="songVersionMeta">
                        <span className="metaChip">
                          {t("songs.item.metaStyle")} <b className="metaChipValue">{estilo}</b>
                        </span>
                        <span className={`statusPill status-${String(it.status || "").toLowerCase()}`}>
                          {status}
                        </span>
                        {String(it.status || "").toLowerCase() === "pending" ? (
                          <div className="statusHint">
                            {t("songs.item.pendingHint")}
                          </div>
                        ) : null}

                      </div>
                    </div>

                    <button type="button" className="detailsBtn" onClick={() => goDetails(it.id)}>
                      {t("songs.item.details")}
                    </button>
                  </div>
                );
              };

              return (
                <section key={it.id} className="songCard">
                  <div className="songCardTop">
                    <div className="songCardIcon" aria-hidden>🎵</div>
                    <div className="songCardText">
                      <div className="songCardTitle">{tituloBase}</div>
                      <div className="songCardSub">
                        {t("songs.item.twoVersions")}
                      </div>
                    </div>
                  </div>

                  <div className="songVersions">
                    {renderVersionRow("v1", v1Url)}
                    {renderVersionRow("v2", v2Url)}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
